import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useToast } from './Toast';
import { useAuth } from '../hooks/useAuth';
import { useState } from 'react';
import styled from 'styled-components';
import authService from '../services/authService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1120 0%, #1a1a2e 100%);
  color: white;
  padding: 6rem 1.5rem 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1.5rem;
  padding: 3rem;
  max-width: 450px;
  width: 100%;
  
  @media (max-width: 640px) {
    padding: 2rem;
  }
`;

const Logo = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 2rem;
  
  &::before {
    content: '📱';
    display: block;
    font-size: 3rem;
    margin-bottom: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.95rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SignupLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  
  a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;


function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const { addToast } = useToast();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Utiliser la fonction login du hook useAuth
      const response = await login(formData);
      
      // Message de succès
      addToast('Connexion réussie !', { type: 'success' });
      
      // Petit délai pour s'assurer que l'état est mis à jour
      setTimeout(() => {
        navigate(redirect);
      }, 100);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur lors de la connexion';
      setError(errorMessage);
      addToast(errorMessage, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <PageWrapper>
      <LoginCard>
        <Logo>INZA STORE</Logo>
        <Title>Connexion</Title>
        <Subtitle>
          Accédez à votre compte et profitez de vos avantages exclusifs
        </Subtitle>
        
        
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '1.5rem',
            color: '#ef4444',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="identifier">Email ou Téléphone</Label>
            <Input
              type="text"
              id="identifier"
              name="identifier"
              placeholder="votre@email.com ou votre numéro"
              value={formData.identifier}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </SubmitButton>
        </Form>

        <SignupLink>
          Pas encore membre ? <a href={`/signup?redirect=${encodeURIComponent(redirect)}`}>Créer un compte</a>
        </SignupLink>
      </LoginCard>
    </PageWrapper>
  );
}

export default LoginPage;
